<template>
    <div :class="`paragraph paragraph-align-${align}`" :style="paragraphStyle">
        <div class="paragraph-container">
            <div 
                class="paragraph-content"
                v-html="content"
            ></div>
        </div>
    </div>
</template>

<script>
export default {
    name: "FormParagraph",
    props: {
        content: {
            type: String,
            default: '<p>Paragraf içeriği</p>'
        },
        align: {
            type: String,
            default: 'left'
        },
        marginTop: {
            type: Number,
            default: 0
        },
        marginBottom: {
            type: Number,
            default: 0
        },
        formId: {
            type: String,
            required: true,
        },
        order: {
            type: Number,
            required: true,
        }
    },
    computed: {
        paragraphStyle() {
            return {
                marginTop: `${this.marginTop}px`,
                marginBottom: `${this.marginBottom}px`,
                textAlign: this.align
            };
        }
    },
    mounted() {
        this.processLinks();
        console.log('🔴 Paragraph mounted:', {
            content: this.content,
            align: this.align,
            marginTop: this.marginTop,
            marginBottom: this.marginBottom,
            formId: this.formId,
            order: this.order
        });
    },
    methods: {
        // Linklerin yeni sekmede açılması için target/rel attribute ekler
        processLinks() {
            this.$nextTick(() => {
                if (!this.$el) return;
                const links = this.$el.querySelectorAll('a[href]');
                links.forEach((link) => {
                    link.setAttribute('target', '_blank');
                    link.setAttribute('rel', 'noopener noreferrer');
                });
            });
        }
    },
    watch: {
        content() {
            this.processLinks();
        }
    }
};
</script>

<style lang="scss" scoped>
/* Base Paragraph Styles */
.paragraph {
    width: 100%;
    max-width: 700px;
    margin: clamp(0.5rem, 1.5vw, 1rem) auto;
    padding: clamp(0.625rem, 1.5vw, 0.875rem);
    position: relative;
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    
    background: linear-gradient(145deg, 
        rgba(255, 255, 255, 0.02) 0%,
        rgba(255, 255, 255, 0.01) 100%);
    border-radius: 0.75rem;
    border: 1px solid rgba(255, 255, 255, 0.06);
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    
    &:hover {
        background: linear-gradient(145deg, 
            rgba(255, 255, 255, 0.03) 0%,
            rgba(255, 255, 255, 0.02) 100%);
    }

    .paragraph-container {
        position: relative;
        z-index: 1;
    }

    .paragraph-content {
        font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        line-height: 1.6;
        color: var(--text-primary, rgba(55, 65, 81, 0.9));
        word-wrap: break-word;

        // Typography styles for HTML content
        :deep(p) {
            margin: 0 0 0.75rem 0;
            font-size: clamp(0.875rem, 1.8vw, 1rem);
            line-height: 1.6;

            &:last-child {
                margin-bottom: 0;
            }

            &.ql-align-center {
                text-align: center !important;
            }

            &.ql-align-right {
                text-align: right !important;
            }

            &.ql-align-justify {
                text-align: justify !important;
            }
        }

        :deep(h1) {
            margin: 0 0 1rem 0;
            font-size: clamp(1.5rem, 3.5vw, 2rem);
            font-weight: 700;
            color: var(--text-primary, rgba(31, 41, 55, 0.95));
            line-height: 1.2;
        }

        :deep(h2) {
            margin: 0 0 0.875rem 0;
            font-size: clamp(1.25rem, 3vw, 1.75rem);
            font-weight: 600;
            color: var(--text-primary, rgba(55, 65, 81, 0.9));
            line-height: 1.3;
        }

        :deep(h3) {
            margin: 0 0 0.75rem 0;
            font-size: clamp(1.125rem, 2.5vw, 1.5rem);
            font-weight: 600;
            color: var(--text-primary, rgba(75, 85, 99, 0.9));
            line-height: 1.4;
        }

        :deep(strong) {
            font-weight: 700;
            color: var(--text-primary, rgba(31, 41, 55, 0.95));
        }

        :deep(em) {
            font-style: italic;
            color: var(--text-secondary, rgba(107, 114, 128, 0.8));
        }

        :deep(u) {
            text-decoration: underline;
            text-decoration-color: var(--primary, #6366f1);
            text-decoration-thickness: 2px;
            text-underline-offset: 2px;
        }

        :deep(ul), :deep(ol) {
            margin: 0 0 0.75rem 0;
            padding-left: 1.5rem;

            li {
                margin-bottom: 0.375rem;
                line-height: 1.5;
                font-size: clamp(0.875rem, 1.8vw, 1rem);

                &:last-child {
                    margin-bottom: 0;
                }
            }
        }

        :deep(ul) {
            list-style-type: disc;

            li::marker {
                color: var(--primary, #6366f1);
            }
        }

        :deep(ol) {
            list-style-type: decimal;

            li::marker {
                color: var(--primary, #6366f1);
                font-weight: 600;
            }
        }

        // Quill editor specific styles
        :deep(span[style*="background-color"]) {
            padding: 0.125rem 0.25rem;
            border-radius: 0.25rem;
        }

        :deep(a) {
            color: var(--primary, #6366f1);
            text-decoration: underline;
            font-weight: 500;
            cursor: pointer;
            transition: color 0.2s ease;

            &:hover {
                color: var(--primary-dark, #4f46e5);
                text-decoration: none;
            }
        }
    }
}

/* Left Alignment */
.paragraph-align-left {
    .paragraph-content {
        text-align: left;
    }
}

/* Center Alignment */
.paragraph-align-center {
    .paragraph-content {
        text-align: center;
    }
}

/* Right Alignment */
.paragraph-align-right {
    .paragraph-content {
        text-align: right;
    }
}

/* Justify Alignment */
.paragraph-align-justify {
    .paragraph-content {
        text-align: justify;
    }
}

// Mobil optimizasyonları
@media (max-width: 768px) {
    .paragraph {
        margin: clamp(0.25rem, 1vw, 0.5rem) auto;
        padding: clamp(0.5rem, 1.2vw, 0.75rem);
        
        .paragraph-content {
            :deep(p) {
                font-size: clamp(0.875rem, 2vw, 0.9rem);
                margin-bottom: clamp(0.5rem, 1.2vw, 0.625rem);
            }

            :deep(h1) {
                font-size: clamp(1.375rem, 3.2vw, 1.75rem);
                margin-bottom: clamp(0.75rem, 1.8vw, 0.875rem);
            }

            :deep(h2) {
                font-size: clamp(1.125rem, 2.8vw, 1.5rem);
                margin-bottom: clamp(0.625rem, 1.5vw, 0.75rem);
            }

            :deep(h3) {
                font-size: clamp(1rem, 2.2vw, 1.25rem);
                margin-bottom: clamp(0.5rem, 1.2vw, 0.625rem);
            }

            :deep(ul), :deep(ol) {
                padding-left: 1.25rem;
                margin-bottom: clamp(0.5rem, 1.2vw, 0.625rem);

                li {
                    font-size: clamp(0.875rem, 2vw, 0.9rem);
                    margin-bottom: clamp(0.25rem, 0.8vw, 0.375rem);
                }
            }
        }
    }
}

@media (max-width: 480px) {
    .paragraph {
        margin: 0.25rem auto;
        padding: 0.5rem;
        
        .paragraph-content {
            :deep(p) {
                font-size: 0.875rem;
                margin-bottom: 0.5rem;
            }

            :deep(h1) {
                font-size: 1.375rem;
                margin-bottom: 0.75rem;
            }

            :deep(h2) {
                font-size: 1.125rem;
                margin-bottom: 0.625rem;
            }

            :deep(h3) {
                font-size: 1rem;
                margin-bottom: 0.5rem;
            }

            :deep(ul), :deep(ol) {
                padding-left: 1.25rem;
                margin-bottom: 0.5rem;

                li {
                    font-size: 0.875rem;
                    margin-bottom: 0.25rem;
                }
            }
        }
    }
}

/* Dark mode devre dışı - Her zaman açık tema kullan */

/* Reduced Motion Support */
@media (prefers-reduced-motion: reduce) {
    .paragraph {
        transition: none;
        
        &:hover {
            transform: none;
        }
    }
}

/* High Performance Mode */
.paragraph {
    will-change: background, transform;
    
    .paragraph-content {
        will-change: auto;
    }
}
</style>
